import { Col, Form, Row } from 'antd';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import SelectComponent from 'components/atoms/SelectComponent';
import TableComponent from 'components/atoms/TableComponent';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useProfily from 'hooks/useProfily';

import { editaceTextuGet } from 'services/editaceTextu/editaceTextu';

import trueFalseSelect from 'data/codebooks/trueFalseSelect';

import getLanguageObject from 'utils/getLanguageObject';

interface IPrehledTextu {}
interface ITableData {
    id: string;
    czpuv: string;
    cznov: string;
    enpuv: string;
    ennov: string;
}
interface ITableParams {
    id?: string;
    czpuv?: string;
    cznov?: string;
    enpuv?: string;
    ennov?: string;
    jenZmenene: boolean;
}

const Prehled: React.FC<IPrehledTextu> = () => {
    const { editaceTextu, serviceMode } = useContext(Context);
    const [form] = Form.useForm();
    const { setLevel } = useProfily();
    const [tableParams, setTableParams] = useState<ITableParams>({
        jenZmenene: true
    });
    const [tableData, setTableData] = useState<ITableData[] | []>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);

    const getTableData: () => ITableData[] = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data: any = [];
        const czLocal = getLanguageObject('cz');
        const enLocal = getLanguageObject('en');
        Object.keys(czLocal).map(e =>
            data.push({
                id: e,
                czpuv: czLocal[e],
                cznov: editaceTextu && editaceTextu.cz[e] ? editaceTextu.cz[e] : '',
                enpuv: enLocal[e],
                ennov: editaceTextu && editaceTextu.en[e] ? editaceTextu.en[e] : ''
            })
        );
        const dataFiltered = data.filter((e: ITableData) => e.cznov !== e.czpuv || e.enpuv !== e.ennov);
        return dataFiltered;
    };

    useEffect(() => {
        !editaceTextu &&
            editaceTextuGet().then(data =>
                setLevel('editaceTextu', {
                    id: data.id,
                    cz: data.cz ? JSON.parse(data.cz) : {},
                    en: data.en ? JSON.parse(data.en) : {}
                })
            );
        !serviceMode && setLevel('serviceMode', true);
        return () => {
            !serviceMode && setLevel('serviceMode', false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setTableData(() => {
            let filteredData = getTableData();
            if (tableParams.jenZmenene) {
                const filtered = filteredData.filter(e =>
                    Object.keys({
                        ...editaceTextu?.cz,
                        ...editaceTextu?.en
                    }).includes(e.id)
                );
                filteredData = filtered;
            }
            if (tableParams.id) {
                const filtered = filteredData.filter(e =>
                    e.id.toLowerCase().includes((tableParams.id || '').toLowerCase())
                );
                filteredData = filtered;
            }
            if (tableParams.czpuv) {
                const filtered = filteredData.filter(e =>
                    e.czpuv.toLowerCase().includes((tableParams.czpuv || '').toLowerCase())
                );
                filteredData = filtered;
            }
            if (tableParams.cznov) {
                const filtered = filteredData.filter(e =>
                    e.cznov.toLowerCase().includes((tableParams.cznov || '').toLowerCase())
                );
                filteredData = filtered;
            }
            if (tableParams.enpuv) {
                const filtered = filteredData.filter(e =>
                    e.enpuv.toLowerCase().includes((tableParams.enpuv || '').toLowerCase())
                );
                filteredData = filtered;
            }
            if (tableParams.ennov) {
                const filtered = filteredData.filter(e =>
                    e.ennov.toLowerCase().includes((tableParams.ennov || '').toLowerCase())
                );
                filteredData = filtered;
            }
            setCurrentPage(1);
            return filteredData;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableParams, editaceTextu]);

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    const onFinish = (values: any) =>
        setTableParams({
            ...(values.id && {
                id: values.id
            }),
            ...(values.czpuv && {
                czpuv: values.czpuv
            }),
            ...(values.cznov && {
                cznov: values.cznov
            }),
            ...(values.enpuv && {
                enpuv: values.enpuv
            }),
            ...(values.ennov && {
                ennov: values.ennov
            }),
            jenZmenene: (values.jenZmenene && values.jenZmenene === 'true') || false
        });

    const columns = [
        {
            title: <TranslateText>IDENTIFIKATOR</TranslateText>,
            dataIndex: 'id',
            key: 'id'
        },
        {
            title: <TranslateText>CZ_PUVODNI</TranslateText>,
            dataIndex: 'czpuv',
            key: 'czpuv'
        },
        {
            title: <TranslateText>CZ_NOVY</TranslateText>,
            dataIndex: 'cznov',
            key: 'cznov'
        },
        {
            title: <TranslateText>EN_PUVODNI</TranslateText>,
            dataIndex: 'enpuv',
            key: 'enpuv'
        },
        {
            title: <TranslateText>EN_NOVY</TranslateText>,
            dataIndex: 'ennov',
            key: 'ennov'
        },
        serviceMode
            ? {
                  dataIndex: 'id',
                  key: 'id',
                  render: (id: string) => (
                      <a>
                          <TranslateText languageEditID={id}>ZMENIT</TranslateText>
                      </a>
                  )
              }
            : {}
    ];

    return (
        <>
            <FormWrapper onFinish={onFinish} outForm={form}>
                <Row gutter={8}>
                    <Col span={4}>
                        <InputComponent label="IDENTIFIKATOR" name="id" notRequired={true} disableEdit={true} />
                    </Col>
                    <Col span={4}>
                        <InputComponent label="CZ_PUVODNI" name="czpuv" notRequired={true} disableEdit={true} />
                    </Col>
                    <Col span={4}>
                        <InputComponent label="CZ_NOVY" name="cznov" notRequired={true} disableEdit={true} />
                    </Col>
                    <Col span={4}>
                        <InputComponent label="EN_PUVODNI" name="enpuv" notRequired={true} disableEdit={true} />
                    </Col>
                    <Col span={4}>
                        <InputComponent label="EN_NOVY" name="ennov" notRequired={true} disableEdit={true} />
                    </Col>
                    <Col span={4}>
                        <SelectComponent
                            disableEdit={true}
                            name="jenZmenene"
                            labelParagraph="ME_ZMENENE_POZADAVKY"
                            notRequired={true}
                            isFormItem={true}
                            defaultValue={'true'}
                            data={trueFalseSelect}
                            allowClear={false}
                        />
                    </Col>
                </Row>
                <Row className="button-row-modal">
                    <ButtonComponent
                        disableEdit={true}
                        buttontext={'FILTROVAT'}
                        onClick={() => form.submit()}
                        id="isinTableFiltrovat"
                    />
                    <ButtonComponent
                        disableEdit={true}
                        buttontext={'VYMAZAT_FILTR'}
                        onClick={() => {
                            form.resetFields();
                            setTableParams({
                                jenZmenene: true
                            });
                        }}
                        htmlType="button"
                        id="isinTableClearFiltr"
                    />
                </Row>
            </FormWrapper>
            <TableComponent
                rowKey={(_, i) => i || 0}
                columns={columns}
                dataSource={tableData}
                locale={{
                    emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                }}
                pagination={{
                    total: tableData?.length,
                    pageSize: 20,
                    showSizeChanger: false,
                    current: currentPage,
                    onChange: page => setCurrentPage(page)
                }}
            />
            <Row className="button-row">
                <ButtonComponent
                    id="isinTableZpetBtn"
                    buttontext="ZPET"
                    type="default"
                    onClick={() => {
                        window.scrollTo(0, 0);
                    }}
                />
            </Row>
        </>
    );
};

export default Prehled;
